<template>
    <!-- @vue-generic {BlastForecastItemAggregated} -->
    <vx-chart-calendar
        data-test="vx-chart-calendar"
        :data="data"
        :valueKey="props.estimatedCost ? 'estimated_cost' : 'num_messages'"
        :loading="loading"
        :error="Boolean(error)"
        legendLabel="Messages"
        :timezone="props.timezone"
        :initialPage="calendarMinPage"
        :minPage="calendarMinPage"
        :maxPage="calendarMaxPage"
        :striped="FEATURES.contact_groups"
        :stripedKey="FEATURES.contact_groups ? 'reserved_date_id' : undefined"
        :stripedLabel="FEATURES.contact_groups ? 'Background pattern marks days reserved by corporate.' : undefined"
    >
        <template v-slot:title>{{ props.estimatedCost ? 'Estimated Cost by Day' : 'Messages by Day' }}</template>

        <template v-slot:dayContent="{ day, item }">
            <vx-popover
                v-if="item && (item.num_blasts > 0 || (FEATURES.contact_groups && item.reserved_date_id))"
                teleport
                hover
                arrow
                placement="top"
                class="grow flex! [&>div]:grow [&>div]:flex"
            >
                <div
                    @click="emit('dayClick', item)"
                    class="grow flex flex-col justify-between px-0.5 @min-[48px]/day-content:py-1 @min-[48px]/day-content:px-1.5"
                    :class="{
                        'cursor-pointer': item,
                    }"
                >
                    <div class="flex justify-between items-center">
                        <div
                            :class="[
                                day.isToday && day.inMonth && item?.intensity
                                    ? ['700', '900'].includes(item?.intensity)
                                        ? 'text-white'
                                        : 'text-sky-950'
                                    : '',
                                {
                                    'font-bold': day.isToday && day.inMonth,
                                },
                            ]"
                        >
                            {{ day.isToday && day.inMonth ? 'Today' : vxFormatOrdinal(day.day) }}
                        </div>
                        <div v-if="FEATURES.contact_groups">
                            <font-awesome-icon
                                v-if="item && item.num_corporate > 0 && day.inMonth"
                                class="text-xs"
                                :class="{
                                    'text-sky-300':
                                        (props.estimatedCost ? item?.cost_intensity : item?.intensity) !== 'default' &&
                                        (props.estimatedCost ? item?.cost_intensity : item?.intensity) !== 'past',
                                }"
                                :icon="faCrown"
                            />
                        </div>
                    </div>
                    <div v-if="day.inMonth && item && item.num_blasts > 0" class="flex items-center">
                        <font-awesome-icon
                            v-if="props.estimatedCost"
                            widthAuto
                            class="text-[10px] mr-px"
                            :icon="faDollar"
                        />

                        <span class="font-bold text-[8px] @min-[48px]/day-content:text-[10px] mr-1">
                            {{
                                props.estimatedCost
                                    ? vxFormatCentsAsDollars(item.estimated_cost, false)
                                    : vxFormatCount(item.num_messages)
                            }}
                        </span>

                        <font-awesome-icon
                            v-if="!props.estimatedCost"
                            class="text-[10px] hidden! @min-[48px]/day-content:inline-block!"
                            :icon="faMessage"
                        />
                    </div>
                </div>

                <template v-if="item" v-slot:content>
                    <div class="w-48 text-sm flex flex-col gap-2">
                        <div class="font-bold">
                            {{ dayjs.utc(day.date).tz(getClientTz()).format('MMM Do, YYYY') }}
                        </div>
                        <div v-if="FEATURES.contact_groups && item.reserved_date_id" class="flex flex-col gap-0.5">
                            <div class="text-xs text-amber-600 font-semibold">Reserved Date</div>
                            <div v-if="item.reserved_date_reason" class="text-xs text-slate-500">
                                {{ item.reserved_date_reason }}
                            </div>
                        </div>
                        <div
                            v-if="
                                FEATURES.contact_groups &&
                                (item.num_corporate > 0 || item.num_org_units > 0 || item.num_groups > 0)
                            "
                            class="flex gap-1"
                        >
                            <meta-badge v-if="item.num_corporate > 0" :icon="faCrown" color="amber">
                                {{ item.num_corporate }}
                            </meta-badge>
                            <meta-badge v-if="item.num_org_units > 0" :icon="faBuildingFlag" color="violet">
                                {{ item.num_org_units }}
                            </meta-badge>
                            <meta-badge v-if="item.num_groups > 0" :icon="faGrid2Plus" color="slate">
                                {{ item.num_groups }}
                            </meta-badge>
                        </div>
                        <div v-if="item.num_blasts > 0" class="text-xs flex flex-col gap-1">
                            <div class="flex justify-between items-center">
                                <div class="text-slate-500">Blasts</div>
                                <div>{{ vxFormatCount(item.num_blasts) }}</div>
                            </div>
                            <div class="flex justify-between items-center">
                                <div class="text-slate-500">Estimated Messages:</div>
                                <div>{{ vxFormatCount(item.num_messages) }}</div>
                            </div>
                            <div v-if="showEstimatedCost" class="flex justify-between items-center">
                                <div class="text-slate-500">Estimated Cost:</div>
                                <div>{{ vxFormatCentsAsDollars(item.estimated_cost, true) }}</div>
                            </div>
                        </div>
                    </div>
                </template>
            </vx-popover>
        </template>

        <template v-slot:footerRight>
            <vx-button data-test="explain-button" color="transparent" size="xs" @click="explainModal = true">
                <font-awesome-icon widthAuto :icon="faCircleQuestion" />
            </vx-button>

            <vx-modal data-test="explain-modal" size="lg" v-model:visible="explainModal" :padding="false">
                <template v-slot:header>
                    <div class="p-6 bg-white border-b border-solid border-b-slate-100">Blast Forecast Calendar</div>
                </template>

                <div class="p-6 bg-slate-50 [&_p_strong]:text-slate-700 flex flex-col gap-4 lg:py-10 lg:px-12">
                    <div class="flex flex-col gap-2 items-start justify-between sm:flex-row sm:items-center sm:gap-4">
                        <p>
                            <strong>Color Gradient</strong> <br />
                            Shows the % of your total reachable audience scheduled to receive messages that day. Darker
                            colors = higher % of audience reached.
                        </p>

                        <div :class="boxClasses">
                            <div
                                v-for="intensityLevel in chartCalendarIntensityLevels.filter((key: string) => !isNaN(Number(key)))"
                                :key="'intensity-' + intensityLevel"
                                class="w-6 h-6 rounded-xs border-black/5"
                                :class="[chartCalendarIntensities[intensityLevel]]"
                            ></div>
                        </div>
                    </div>

                    <div class="flex flex-col gap-2 items-start justify-between sm:flex-row sm:items-center sm:gap-4">
                        <p>
                            <strong>Estimated Messages (#)</strong> <br />
                            This estimate is based on the current number of contacts in each Blast’s audience. The final
                            message count may vary if audience eligibility or message variables change before the Blast
                            is sent.
                        </p>

                        <div :class="boxClasses">
                            <div
                                class="w-15.75 flex flex-col pt-1 pr-2 pl-1.5 pb-1.5 justify-between items-start rounded-[5px] border-[0.5px] border-sky-700 bg-sky-600 text-sky-200"
                            >
                                <span class="text-[8px] leading-4"> 18th </span>
                                <div class="flex px-0.5 gap-0.5 text-[10px] items-center justify-start">
                                    <span class="font-bold leading-4">1.14M</span>
                                    <font-awesome-icon size="sm" :icon="faMessage" widthAuto />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="FEATURES.contact_groups"
                        class="flex flex-col gap-2 items-start justify-between sm:flex-row sm:items-center sm:gap-4"
                    >
                        <p>
                            <strong>Background Pattern</strong> <br />
                            A diagonal stripe pattern indicates a date reserved by Corporate.
                        </p>

                        <div :class="boxClasses">
                            <div
                                class="w-6 h-6 rounded-xs border-black/5 bg-sky-200 border-sky-300"
                                :style="stripedLegendStyle"
                            ></div>
                        </div>
                    </div>

                    <div v-if="showEstimatedCost" class="flex flex-col gap-4">
                        <div
                            class="flex flex-col gap-2 items-start justify-between sm:flex-row sm:items-center sm:gap-4"
                        >
                            <div>
                                <strong class="text-slate-700">Estimated Costs ($)</strong>
                                <p>
                                    This is only an estimate, as the audience segment can vary in size prior to sending.
                                </p>
                            </div>
                            <div :class="boxClasses">
                                <div
                                    class="w-15.75 flex flex-col pt-1 pr-2 pl-1.5 pb-1.5 justify-between items-start rounded-[5px] border-[0.5px] border-sky-700 bg-sky-600 text-sky-200"
                                >
                                    <span class="text-[8px] leading-4"> 18th </span>
                                    <div class="flex px-0.5 gap-0.5 text-[10px] items-center justify-start">
                                        <font-awesome-icon size="sm" :icon="faDollar" widthAuto />
                                        <span class="font-bold leading-4">1.14M</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <span>Estimated Costs are calculated using:</span>
                            <ul class="pl-6 list-disc">
                                <li>Message Type: SMS or MMS</li>
                                <li>Quantity of SMS Segments (message length)</li>
                                <li>Contracted rates (SMS or MMS rate + Carrier Fee)</li>
                                <li>Quantity of contacts within the audience at time of estimation</li>
                            </ul>
                        </div>
                        <p>
                            Messages that contain variables can also cause the estimate to diverge from the actual cost
                            at send time.
                        </p>
                    </div>

                    <span class="text-xl font-bold text-slate-700">When clicking a day</span>

                    <template v-if="FEATURES.contact_groups">
                        <div
                            class="flex flex-col gap-2 items-start justify-between sm:flex-row sm:items-center sm:gap-4"
                        >
                            <p>
                                <strong>Corporate Blasts</strong> <br />
                                Number of campaigns scheduled by Corporate (Franchisor).
                            </p>

                            <div :class="boxClasses">
                                <meta-badge :icon="faCrown" color="amber">Corporate</meta-badge>
                            </div>
                        </div>

                        <div
                            class="flex flex-col gap-2 items-start justify-between sm:flex-row sm:items-center sm:gap-4"
                        >
                            <p>
                                <strong>Organizational Units Blasts</strong> <br />
                                Number of campaigns scheduled by holding groups (entities that own multiple
                                franchisees/locations).
                            </p>

                            <div :class="boxClasses">
                                <meta-badge :icon="faBuildingFlag" color="violet">OU</meta-badge>
                            </div>
                        </div>

                        <div
                            class="flex flex-col gap-2 items-start justify-between sm:flex-row sm:items-center sm:gap-4"
                        >
                            <p>
                                <strong>Group Blasts</strong> <br />
                                Number of campaigns scheduled by individual Groups/Locations.
                            </p>

                            <div :class="boxClasses">
                                <meta-badge :icon="faGrid2Plus" color="slate">Group</meta-badge>
                            </div>
                        </div>

                        <p>
                            <strong>Total Blasts</strong> <br />
                            Sum of all campaigns across Corporate, Organizational Units based Groups, and Groups.
                        </p>
                    </template>
                    <p v-else>
                        <strong>Total Blasts</strong> <br />
                        Sum of all campaigns across Corporate, Holding Groups, and Groups.
                    </p>

                    <p>
                        <strong>Total Messages</strong> <br />
                        Overall messages planned to be sent that day (audience size × # of messages per blast).
                    </p>
                </div>
            </vx-modal>
        </template>
    </vx-chart-calendar>
</template>

<script setup lang="ts">
import { faBuildingFlag, faCrown, faDollar, faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';
import { faCircleQuestion, faMessage } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxChartCalendar,
    vxFormatCentsAsDollars,
    vxFormatCount,
    vxFormatOrdinal,
    VxPopover,
    VxModal,
    chartCalendarIntensities,
    chartCalendarIntensityLevels,
} from '@voxie/frontend-components';
import { computed, ref, watch, type PropType, inject } from 'vue';
import type { GetBlastForecastRequest } from '~/services/blastForecast/blastForecastClient.types';
import dayjsLib from '~/utils/dayjs';
import MetaBadge from '~/components/general/MetaBadge.vue';
import { getClientTz } from '~/utils/date';
import { useBlastForecastSummary, type BlastForecastItemAggregated } from '../composables/useBlastForecastSummary';
import { stripedLegendStyle } from '../constants/stripedPattern';
import type { Features } from '~/provides/features.types';

const dayjs = dayjsLib;
const teamId = Spark.state.currentTeam.id;

const FEATURES = inject('FEATURES') as Features;

const boxClasses =
    'bg-white rounded-lg p-3 flex gap-1 shadow-[0_1px_3px_0_rgba(5,71,107,0.15),0_1px_2px_0_rgba(5,71,107,0.07)]';

const props = defineProps({
    fetchParams: {
        type: Object as PropType<GetBlastForecastRequest>,
        required: true,
    },
    timezone: String,
    orgUnitId: Number,
    showEstimatedCost: {
        type: Boolean,
        required: true,
    },
    estimatedCost: Boolean,
});

const emit = defineEmits<{ (e: 'dayClick', item: BlastForecastItemAggregated): void }>();

const explainModal = ref(false);
const { data, loading, error, fetchData } = useBlastForecastSummary();

const calendarMinPage = computed(() => {
    const date = dayjs.utc(props.fetchParams.range[0]).tz(getClientTz());
    return {
        year: date.year(),
        month: date.month() + 1,
    };
});

const calendarMaxPage = computed(() => {
    const date = dayjs.utc(props.fetchParams.range[1]).tz(getClientTz());
    return {
        year: date.year(),
        month: date.month() + 1,
    };
});

watch(
    [() => props.fetchParams],
    () => {
        fetchData({ teamId: teamId, orgUnitId: props.orgUnitId }, props.fetchParams, FEATURES.contact_groups);
    },
    { immediate: true, deep: true }
);
</script>
